import { useEffect, useState } from 'react'
import { GTDialog, GTDialogBody, GTDialogFooter, GTDialogHeading } from '@/components/ui/gt-dialog'
import { cn } from '@/lib/utils'
import { Archive, FileText, LucideIcon } from 'lucide-react'
import { useGetTasksV4 } from '../../services/api/tasks.hooks'
import { useGoalCreation } from './creation/shared/GoalCreationContext'
import { linkedTasksForGoal } from './goalTasks'
import { TGoalTaskHandling } from './goalTypes'

/**
 * The archive moment. Archiving is recoverable — the goal comes back from "Show archived" — with one
 * exception the user has to see before they commit: the goal's progress history is discarded and is
 * not restored. That line is not small print; it sits in the dialog body.
 *
 * When the goal has linked tasks their fate is an explicit choice with no default. Both outcomes are
 * legitimate and they are not the same, so the confirm stays disabled until one is picked rather than
 * quietly doing the safer-looking thing.
 *
 * Styling follows the quiet GT shell: hairline rows, zinc text, the yellow --action token reserved
 * for the primary confirm. Archive is consequential, not an emergency, so nothing here is red.
 */

interface TTaskHandlingChoice {
    value: TGoalTaskHandling
    icon: LucideIcon
    title: string
    description: (count: number) => string
}

const TASK_HANDLING_CHOICES: TTaskHandlingChoice[] = [
    {
        value: 'keep',
        icon: FileText,
        title: 'Keep the tasks',
        description: (n) => `${n === 1 ? 'It stays' : 'They stay'} in your lists, no longer filed under this goal.`,
    },
    {
        value: 'archive',
        icon: Archive,
        title: 'Archive the tasks too',
        description: (n) => `${n === 1 ? 'It goes' : 'They go'} away with the goal and leave active task lists.`,
    },
]

/** The explicit keep-or-archive choice for a goal's tasks, shared by the archive and achieve dialogs. */
export const TaskHandlingChoices = ({
    label,
    taskCount,
    value,
    onChange,
}: {
    label: string
    taskCount: number
    value: TGoalTaskHandling | null
    onChange: (value: TGoalTaskHandling) => void
}) => (
    <div className="mt-1 flex flex-col gap-2" role="radiogroup" aria-label={label}>
        {TASK_HANDLING_CHOICES.map((choice) => (
            <button
                key={choice.value}
                type="button"
                role="radio"
                aria-checked={value === choice.value}
                onClick={() => onChange(choice.value)}
                className={cn(
                    'flex w-full items-start gap-3 rounded-gt-md border border-border bg-card px-3 py-3 text-left',
                    'transition-[background-color,border-color] duration-150 hover:bg-muted/70',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    value === choice.value && 'border-foreground/30 bg-muted'
                )}
            >
                <span
                    className={cn(
                        'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-border bg-background',
                        value === choice.value && 'border-foreground'
                    )}
                    aria-hidden="true"
                >
                    {value === choice.value && <span className="size-2 rounded-full bg-foreground" />}
                </span>
                <div className="mt-px flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <choice.icon className="size-4" />
                </div>
                <div className="min-w-0">
                    <p className="text-body-sm font-medium text-foreground">{choice.title}</p>
                    <p className="mt-0.5 text-body-sm text-muted-foreground">{choice.description(taskCount)}</p>
                </div>
            </button>
        ))}
    </div>
)

const ArchiveGoalDialog = () => {
    const { allGoals, archiveGoalId, archiveGoalOptions, setArchiveGoalId, archiveGoal, taskGoalLinks } =
        useGoalCreation()
    const options = archiveGoalOptions ?? undefined
    // The weekly review's "Archive as done": the same archive, recorded as a goal that was finished.
    const isDone = options?.reason === 'done'
    const { data: allTasks } = useGetTasksV4()
    const goal = allGoals.find((g) => g.id === archiveGoalId)
    const [taskHandling, setTaskHandling] = useState<TGoalTaskHandling | null>(null)

    // A fresh choice per goal: a decision made for the last goal must not carry into this one.
    useEffect(() => setTaskHandling(null), [archiveGoalId])

    const linkedTasks = goal ? linkedTasksForGoal(goal.id, allTasks, taskGoalLinks) : []
    const taskCount = linkedTasks.length
    const close = () => setArchiveGoalId(null)
    const confirm = () => {
        if (!goal) return
        // With no tasks to decide about there is nothing to choose, so "keep" is not a default —
        // it is the only thing archiving can mean here.
        if (taskCount === 0) return archiveGoal(goal.id, 'keep', options)
        if (taskHandling) archiveGoal(goal.id, taskHandling, options)
    }

    return (
        <GTDialog
            open={!!goal}
            onOpenChange={(next) => !next && close()}
            label="Archive goal"
            description="Archive this goal and choose what happens to its tasks."
            onSubmit={confirm}
        >
            {goal && (
                <GTDialogBody className="gap-3">
                    <GTDialogHeading
                        title={`Archive “${goal.title}”${isDone ? ' as done' : ''}?`}
                        subtitle="It leaves your goals list and can’t take new work. You can restore it from Show archived."
                    />
                    <p className="text-body-sm text-secondary-foreground">
                        Progress history for this goal won’t be kept, even if you restore it.
                    </p>

                    {taskCount > 0 && (
                        <TaskHandlingChoices
                            label={`What happens to ${taskCount} linked ${taskCount === 1 ? 'task' : 'tasks'}`}
                            taskCount={taskCount}
                            value={taskHandling}
                            onChange={setTaskHandling}
                        />
                    )}

                    <GTDialogFooter
                        onCancel={close}
                        confirmLabel={isDone ? 'Archive as done' : 'Archive goal'}
                        onConfirm={confirm}
                        confirmDisabled={taskCount > 0 && !taskHandling}
                    />
                </GTDialogBody>
            )}
        </GTDialog>
    )
}

export default ArchiveGoalDialog
